import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playPopSound, playWinFanfare } from '../utils/soundEffects';
import { getSettings } from '../utils/db';

const { FiZap } = FiIcons;

const BALLOONS = [
  { id: 1, color: 'from-rose-500 to-red-600', shadow: 'rgba(239,68,68,0.5)', label: '🎈 1' },
  { id: 2, color: 'from-amber-400 to-yellow-500', shadow: 'rgba(245,158,11,0.5)', label: '🎈 2' },
  { id: 3, color: 'from-blue-500 to-indigo-600', shadow: 'rgba(59,130,246,0.5)', label: '🎈 3' },
  { id: 4, color: 'from-emerald-400 to-teal-600', shadow: 'rgba(16,185,129,0.5)', label: '🎈 4' },
  { id: 5, color: 'from-fuchsia-500 to-pink-600', shadow: 'rgba(236,72,153,0.5)', label: '🎈 5' },
  { id: 6, color: 'from-purple-500 to-violet-700', shadow: 'rgba(139,92,246,0.5)', label: '🎈 6' }
];

const BalloonPopGame = ({ prizes = [], onFinished, isDarkMode = false }) => {
  const [poppedId, setPoppedId] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [wonPrize, setWonPrize] = useState('');

  const safePrizes = prizes && prizes.length > 0 ? prizes : [
    "🧢 School Champion Cap", 
    "🔬 Science Whiz Badge", 
    "🎁 Mega Stationery Kit", 
    "⭐ Star Student Trophy Badge",
    "📚 Book Voucher",
    "🍿 Carnival Treat Token"
  ];

  const handlePop = (balloon) => {
    if (poppedId !== null) return;
    setPoppedId(balloon.id);

    const settings = getSettings();
    if (settings.soundEnabled !== false) {
      playPopSound();
    }

    const randomPrize = safePrizes[Math.floor(Math.random() * safePrizes.length)];
    setWonPrize(randomPrize);

    setTimeout(() => {
      setIsRevealed(true);
      if (settings.soundEnabled !== false) {
        playWinFanfare();
      }
      setTimeout(() => {
        if (onFinished) onFinished(randomPrize);
      }, 1800);
    }, 450);
  };

  return (
    <div className="relative w-full max-w-xl mx-auto py-6 select-none flex flex-col items-center">
      
      {/* Title instructions */}
      <div className="text-center mb-6">
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 mb-2 shadow-xs ${
          isDarkMode 
            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
            : 'bg-amber-100 text-amber-800 border border-amber-300'
        }`}>
          <SafeIcon icon={FiZap} /> Kids Carnival Stall Game
        </span>
        <h3 className={`text-2xl md:text-3xl font-extrabold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          Tap Any Balloon to Pop!
        </h3>
        <p className={`text-xs md:text-sm mt-1 font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
          Pick your lucky balloon to burst and reveal your school gift.
        </p>
      </div>

      {/* Balloon Floating Grid */}
      <div className="grid grid-cols-3 gap-6 md:gap-8 w-full px-4 justify-items-center min-h-[300px] items-center">
        {BALLOONS.map((b, idx) => {
          const isThisPopped = poppedId === b.id;
          const isOtherPopped = poppedId !== null && !isThisPopped;

          return (
            <div key={b.id} className="relative flex flex-col items-center">
              <AnimatePresence>
                {!isThisPopped && (
                  <motion.div
                    onClick={() => handlePop(b)}
                    initial={{ y: 0 }}
                    animate={{ 
                      y: [0, -14, 0],
                      rotate: [-2, 2, -2]
                    }}
                    transition={{ 
                      repeat: Infinity, 
                      duration: 2.2 + idx * 0.3,
                      ease: "easeInOut" 
                    }}
                    whileHover={{ scale: 1.1, cursor: "pointer" }}
                    whileTap={{ scale: 0.9 }}
                    className={`relative w-20 h-24 sm:w-24 sm:h-28 rounded-full bg-gradient-to-t ${b.color} shadow-lg cursor-pointer flex flex-col items-center justify-center transition-opacity ${isOtherPopped ? 'opacity-30' : 'opacity-100'}`}
                    style={{ 
                      borderRadius: '50% 50% 50% 50% / 40% 40% 60% 60%',
                      boxShadow: `0 12px 25px ${b.shadow}`
                    }}
                  >
                    {/* Balloon Specular Highlight */}
                    <div className="absolute top-3 left-3 w-5 h-8 rounded-full bg-white/40 blur-[1px] rotate-[-25deg] pointer-events-none"></div>

                    {/* Balloon Knot & String */}
                    <div className="absolute -bottom-2 w-3 h-2 bg-inherit rounded-sm"></div>
                    <div className={`absolute -bottom-10 w-0.5 h-8 ${isDarkMode ? 'bg-slate-300/40' : 'bg-slate-500/50'}`}></div>

                    <span className="text-white font-black text-sm drop-shadow-md">
                      {idx + 1}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Pop burst particle effect */}
              {isThisPopped && !isRevealed && (
                <motion.div 
                  initial={{ scale: 0.8, opacity: 1 }}
                  animate={{ scale: 2, opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="w-20 h-20 rounded-full border-4 border-amber-300 flex items-center justify-center"
                >
                  <span className="text-3xl">💥</span>
                </motion.div>
              )}
            </div>
          );
        })}
      </div>

      {/* Prize Reveal Banner */}
      <AnimatePresence>
        {isRevealed && (
          <motion.div
            initial={{ scale: 0.7, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            className={`mt-6 p-6 rounded-3xl border-2 text-center shadow-2xl backdrop-blur-md max-w-sm w-full mx-auto ${
              isDarkMode 
                ? 'bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 border-amber-400 text-white' 
                : 'bg-white border-amber-400 text-slate-900 shadow-xl'
            }`}
          >
            <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-amber-400 to-yellow-300 text-slate-950 font-black text-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
              🎁
            </div>
            <span className="text-xs uppercase font-extrabold text-amber-500 tracking-wider">Congratulations!</span>
            <h4 className={`text-xl md:text-2xl font-black mt-1 mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{wonPrize}</h4>
            <p className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Your school event coupon is being prepared...</p>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default BalloonPopGame;
